/**
 * AI Event Extraction Service
 *
 * Calls the backend AI endpoints to extract event data from text or images.
 * Authentication requires a Bearer token (user must be logged in).
 */

import { apiFetch } from 'ts-core';
import type { AiExtractResponse } from '../types';

export class AiExtractError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'AiExtractError';
  }
}

export interface AiServiceConfig {
  apiEndpoint: string;
  apiKey: string;
  token: string;
}

/**
 * Extract event data from a plain-text description.
 * Calls POST /api/v2/ai/event/from-text
 */
export async function extractEventsFromText(
  config: AiServiceConfig,
  text: string,
  locale = 'en'
): Promise<AiExtractResponse> {
  const response = await apiFetch(`${config.apiEndpoint}/api/v2/ai/event/from-text`, {
    method: 'POST',
    apiKey: config.apiKey,
    authToken: config.token,
    body: JSON.stringify({ text, locale }),
  });

  if (!response.ok) {
    throw new AiExtractError(response.status, `text extraction failed with status ${response.status}`);
  }

  return response.json() as Promise<AiExtractResponse>;
}

/**
 * Extract event data from an uploaded image (poster, flyer, bulletin board).
 * Calls POST /api/v2/ai/event/from-image (multipart/form-data)
 */
export async function extractEventsFromImage(
  config: AiServiceConfig,
  file: File,
  locale = 'en'
): Promise<AiExtractResponse> {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('locale', locale);

  const response = await fetch(`${config.apiEndpoint}/api/v2/ai/event/from-image`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${config.token}`,
      'X-Api-Key': config.apiKey,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new AiExtractError(response.status, `image extraction failed with status ${response.status}`);
  }

  return response.json() as Promise<AiExtractResponse>;
}
